import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { useOwnershipDraftQuery } from '../../rest/useChangeOwnership ';
import { useCompanyOwnerQuery } from '../../rest/useCompanyInfo';
import { ICompanyOwnerList } from '../../rest/types';
import CommonOfflinePaymentInfo from '../new-member-applications/IndexFile/paymentInformation/offline/CommonOfflinePaymentInfo';
import axios from 'axios';
import { url } from '../../Helpers/utils/Constant';
import { getToken } from '../../rest/utils/get-token';
import { showToast } from '../../Toaster/Toaster';
import ImageUploaderWithPreview from '../../Common/ImageUploaderWithPreview';
import { API_ENDPOINTS } from '../../rest/utils/api-endpoints';

type IProps = {
  handelClose: Dispatch<SetStateAction<boolean>>;
  setReload?: Dispatch<SetStateAction<boolean>>;
};

const CreateOwnershipApplication = ({ handelClose, setReload }: IProps) => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const { data: ownershipDraftList, fetchData: fetchOwnershipDraft } =
    useOwnershipDraftQuery();
  const draftData = ownershipDraftList?.[0];

  useEffect(() => {
    if (user?.user_member_id) {
      fetchOwnershipDraft({
        ownership_change_application_member_id: user.user_member_id,
        ownership_change_application_status: 'draft',
      });
    }
  }, [user?.user_member_id, fetchOwnershipDraft]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    setError,
    control,
    watch,
    getValues,
  } = useForm<any>({
    defaultValues: {
      bank_ac_no: '00890320000141',
      amount: '',
      bank_name: 'Trust Bank (Kakrail Branch)',
    },
  });

  const amount = watch('amount');
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const token = getToken();
  const { companyOwnerData } = useCompanyOwnerQuery();

  useEffect(() => {
    axios
      .get(`${url}/api/atab/payment-amount?application_type=OWNERSHIP_CHANGE`)
      .then((res) => {
        if (res.data.success) {
          setValue('amount', res.data.data.amount);
        }
      });
  }, [setValue]);

  useEffect(() => {
    if (draftData) {
      if (draftData.ownership_change_application_prev_owner) {
        setValue(
          'ownership_change_application_prev_owner',
          draftData.ownership_change_application_prev_owner,
        );
      }
      if (draftData.company_owner_name) {
        setValue('company_owner_name', draftData.company_owner_name);
      }
      if (draftData.company_owner_nid) {
        setValue('company_owner_nid', draftData.company_owner_nid);
      }
      if (draftData.company_owner_phone) {
        setValue('company_owner_phone', draftData.company_owner_phone);
      }
      if (draftData.company_owner_email) {
        setValue('company_owner_email', draftData.company_owner_email);
      }
      if (draftData.company_owner_designation) {
        setValue(
          'company_owner_designation',
          draftData.company_owner_designation,
        );
      }
      if (draftData.company_owner_address) {
        setValue('company_owner_address', draftData.company_owner_address);
      }
    }
  }, [draftData, setValue]);

  const onSubmit = async (data: any, asDraft: boolean = false) => {
    const formData = new FormData();

    const paymentFields = [
      'type',
      'bank_ac_no',
      'amount',
      'bank_name',
      'deposit_date',
      'branch_name',
      'reference_no',
      'bank_slip',
    ];

    if (asDraft) {
      formData.append('status', 'draft');
      formData.append('ownership_change_application_status', 'draft');
    } else {
      if (data?.type === 'online') {
        formData.append(
          'success_page',
          `${window.location.origin}/payment-success`,
        );
        formData.append(
          'failed_page',
          `${window.location.origin}/payment-failed`,
        );
        formData.append(
          'cancelled_page',
          `${window.location.origin}/payment-reject`,
        );
      }
    }

    Object.keys(data).forEach((item) => {
      if (asDraft && paymentFields.includes(item)) {
        return;
      }
      if (!asDraft && data?.type !== 'offline' && paymentFields.includes(item) && item !== 'type') {
        return;
      }

      if (typeof data[item] === 'object' && data[item] !== null) {
        if (data[item].length > 0) {
          formData.append(item, data[item][0]);
        }
      } else if (
        data[item] !== undefined &&
        data[item] !== null &&
        data[item] !== ''
      ) {
        formData.append(item, data[item]);
      }
    });

    formData.append(
      'ownership_change_application_member_id',
      user.user_member_id?.toString(),
    );

    try {
      setLoading(true);
      setIsDraft(asDraft);
      const response = await fetch(`${url}/api/member/owner-change-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (!asDraft && responseData?.data?.redirect_url) {
          window.open(
            responseData?.data?.redirect_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
        showToast(
          responseData.message ||
          (asDraft
            ? 'Application saved as draft successfully'
            : 'Application submitted successfully'),
          'success',
        );
        queryClient.invalidateQueries([API_ENDPOINTS.OWNERSHIP_APPLICATION]);
        handelClose(false);
        setReload?.((prev: boolean) => !prev);
      } else {
        setLoading(false);
        setIsDraft(false);
        showToast(
          responseData.message ||
          (asDraft
            ? 'Failed to save application as draft'
            : 'Failed to submit application'),
          'error',
        );
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
      setLoading(false);
      setIsDraft(false);
    }
  };

  const handleSaveDraft = () => {
    const values = getValues();
    onSubmit(values, true);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit((data) => onSubmit(data, false))}
      p={2}
    >
      <Grid container spacing={2} mb={2}>
        <Grid xs={12} sm={6} md={4} item>
          <FormControl fullWidth>
            <InputLabel id='prev-owner-select-label'>
              Select a previous company owner{' '}
              <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <Select
              labelId='prev-owner-select-label'
              id='prev-owner-select'
              required
              label='Select a previous company owner *'
              value={watch('ownership_change_application_prev_owner') || ''}
              {...register('ownership_change_application_prev_owner')}
            >
              {companyOwnerData?.length === 0 && (
                <MenuItem disabled>Not found</MenuItem>
              )}
              {companyOwnerData?.map((company: ICompanyOwnerList) => (
                <MenuItem key={company?.company_owner_id} value={company?.company_owner_id}>
                  {company?.company_owner_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>PRESENT OWNER INFORMATION</Divider>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='New company owner name'
            required
            id='outlined-owner-name'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_name')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Company owner NID'
            id='outlined-owner-nid'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_nid')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Company owner phone'
            id='outlined-owner-phone'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_phone')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Company owner email'
            id='outlined-owner-email'
            type='email'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_email')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Company owner designation'
            id='outlined-owner-designation'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_designation')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Company owner address'
            id='outlined-owner-address'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('company_owner_address')}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Company owner photo ( max size 5mb )'
            fileName='company_owner_photo'
            required
            imgSerUrl={draftData?.company_owner_photo || null}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Company owner NID file ( max size 5mb )'
            fileName='company_owner_nid_file'
            required
            imgSerUrl={draftData?.company_owner_nid_file || null}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Company owner bio-data ( max size 5mb )'
            fileName='company_owner_biodata'
            imgSerUrl={draftData?.company_owner_biodata || null}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Company owner passport ( max size 5mb )'
            fileName='company_owner_passport'
            imgSerUrl={draftData?.company_owner_passport || null}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Company owner signature ( max size 5mb )'
            fileName='company_owner_signature'
            required
            imgSerUrl={draftData?.company_owner_signature || null}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Company owner documents</Divider>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Mocat Approval Letter ( max size 5mb )'
            fileName='ownership_change_application_mocat_approval_copy_file'
            required
            imgSerUrl={
              draftData?.ownership_change_application_mocat_approval_copy_file ||
              null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Mocat certificate english file ( max size 5mb )'
            fileName='ownership_change_application_mocat_certificate_english_copy'
            required
            imgSerUrl={
              draftData?.ownership_change_application_mocat_certificate_english_copy ||
              null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Trade license ( max size 5mb )'
            fileName='ownership_change_application_trade_license'
            required
            imgSerUrl={
              draftData?.ownership_change_application_trade_license || null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Proof of Submission of Tax Return (max size 5mb)'
            fileName='ownership_change_application_tax_certificate'
            required
            imgSerUrl={
              draftData?.ownership_change_application_tax_certificate || null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Change deed ( max size 5mb )'
            fileName='ownership_change_application_ownership_change_deed'
            required
            imgSerUrl={
              draftData?.ownership_change_application_ownership_change_deed ||
              null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Forwarding letter ( max size 5mb )'
            fileName='ownership_change_application_forwarding_letter'
            required
            imgSerUrl={
              draftData?.ownership_change_application_forwarding_letter || null
            }
          />
        </Grid>

        <Grid xs={12} item sx={{ my: 2 }}>
          <Divider>
            <Chip label='Payment Information' size='small' />
          </Divider>
        </Grid>
        <Grid xs={12} item>
          <CommonOfflinePaymentInfo
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            register={register}
            setValue={setValue}
            control={control}
            onlinePaymentAmount={amount}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={2}
        justifyContent='flex-end'
        alignItems='center'
        mt={4}
      >
        <Button
          type='button'
          variant='outlined'
          color='primary'
          disabled={loading}
          onClick={handleSaveDraft}
          startIcon={
            loading && isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && isDraft ? 'Saving Draft...' : 'Save As Draft'}</span>
        </Button>

        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={loading}
          disableElevation
          startIcon={
            loading && !isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && !isDraft ? 'Submitting...' : 'Submit'}</span>
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateOwnershipApplication;

